// TenantShell is the layout route component for /t/:slug.
// It resolves tenant auth config, initialises the appropriate auth mechanism,
// and renders the authenticated layout shell (sidebar, header, footer).
//
// Auth mode branching:
//   auth_mode === 'oidc'       → AuthProvider → ProtectedRoute → AuthenticatedShell
//   auth_mode === 'magic_link' → MagicLinkGate (cookie-based session)
//
// The TenantCtx (slug, tenantId, authConfig) is provided to both paths.
// The ApiClient and RootLayout are created inside each path's shell component.
//
// Component hierarchy (OIDC):
//   TenantShell → TenantCtx.Provider → AuthProvider → ProtectedRoute → AuthenticatedShell → BrandedLayout → RootLayout
//
// Component hierarchy (magic link):
//   TenantShell → TenantCtx.Provider → MagicLinkGate → AuthenticatedMagicLinkShell → MagicLinkBrandedLayout → RootLayout

import { useEffect, useState, createContext, useContext, useMemo } from 'react'
import { AuthProvider, useAuth, type AuthProviderProps } from 'react-oidc-context'
import { WebStorageStateStore } from 'oidc-client-ts'
import { useParams } from '@tanstack/react-router'
import { fetchTenantAuthConfig, type TenantAuthConfig } from '@/lib/api/tenant'
import { createApiClient, ApiClientContext, type ApiClient } from '@/lib/api/client'
import { ProtectedRoute } from '@/components/shared/ProtectedRoute'
import { MagicLinkGate } from '@/components/shared/MagicLinkGate'
import { RootLayout } from '@/components/layout/RootLayout'
import { useBranding } from '@/lib/hooks/use-branding'
import { useFetchMe, UserMeProvider } from '@/lib/hooks/use-current-user'

export interface TenantContext {
  slug: string
  tenantId: string
  authConfig: TenantAuthConfig
}

const TenantCtx = createContext<TenantContext | null>(null)

export function useTenant(): TenantContext {
  const ctx = useContext(TenantCtx)
  if (!ctx) {
    throw new Error('useTenant must be used within a TenantShell')
  }
  return ctx
}

/**
 * TenantShell is rendered by the /t/$slug layout route.
 * It reads the slug from router params, fetches auth config,
 * then wraps children in the appropriate auth mechanism + layout.
 */
export function TenantShell() {
  const { slug } = useParams({ from: '/t/$slug' })
  const [authConfig, setAuthConfig] = useState<TenantAuthConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function loadConfig(): Promise<void> {
      try {
        const config = await fetchTenantAuthConfig(slug)
        if (!cancelled) {
          setAuthConfig(config)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tenant configuration')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    setLoading(true)
    loadConfig()

    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (error || !authConfig) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <h2 className="text-lg font-semibold text-destructive">Tenant Not Found</h2>
          <p className="text-sm text-muted-foreground">
            {error ?? 'Unable to load tenant configuration.'}
          </p>
          <p className="text-sm text-muted-foreground">Check the URL and try again.</p>
        </div>
      </div>
    )
  }

  const tenantCtx: TenantContext = { slug, tenantId: authConfig.tenant_id, authConfig }

  // Magic link path — cookie-based session, no OIDC provider
  if (authConfig.auth_mode === 'magic_link') {
    return (
      <TenantCtx.Provider value={tenantCtx}>
        <MagicLinkGate slug={slug} tenantId={authConfig.tenant_id} />
      </TenantCtx.Provider>
    )
  }

  // OIDC path — unchanged from original implementation
  const oidcConfig: AuthProviderProps = {
    authority: authConfig.issuer_url!,
    client_id: authConfig.client_id!,
    redirect_uri: `${window.location.origin}/t/${slug}/auth/callback`,
    post_logout_redirect_uri: `${window.location.origin}/t/${slug}`,
    scope: 'openid profile email',
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    onSigninCallback: () => {
      // Clear the ?code=... query params left by the IdP redirect.
      // Navigation to dashboard is handled by ProtectedRoute after auth settles.
      window.history.replaceState({}, document.title, window.location.pathname)
    },
  }

  return (
    <AuthProvider {...oidcConfig} key={slug}>
      <TenantCtx.Provider value={tenantCtx}>
        <ProtectedRoute>
          <AuthenticatedShell slug={slug} tenantId={authConfig.tenant_id} />
        </ProtectedRoute>
      </TenantCtx.Provider>
    </AuthProvider>
  )
}

/**
 * Inner component for the OIDC path — has access to OIDC auth context.
 * Creates the API client using the OIDC token and provides it via context.
 */
function AuthenticatedShell({ slug, tenantId }: { slug: string; tenantId: string }) {
  const auth = useAuth()

  const apiClient: ApiClient = useMemo(
    () => createApiClient(tenantId, () => auth.user?.id_token),
    [tenantId, auth],
  )

  return (
    <ApiClientContext.Provider value={apiClient}>
      <BrandedLayout slug={slug} tenantId={tenantId} />
    </ApiClientContext.Provider>
  )
}

/**
 * Inner component for the OIDC path — has access to the API client context.
 * Fetches current user context and tenant branding, then renders the layout.
 */
function BrandedLayout({ slug, tenantId }: { slug: string; tenantId: string }) {
  const branding = useBranding()
  const { data: me, isLoading: meLoading } = useFetchMe()

  // Build logo URL only if tenant has a logo
  const logoUrl = branding?.hasLogo ? `/api/v1/tenants/${tenantId}/branding/logo` : undefined

  if (meLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <UserMeProvider value={me ?? null}>
      <RootLayout slug={slug} logoUrl={logoUrl} />
    </UserMeProvider>
  )
}
